import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ADD_ROLE } from '../../../GraphQL/roles/mutations';
import { GET_ROLES } from '../../../GraphQL/roles/queries';
import { GET_LOCALITIES } from '../../../GraphQL/settings/queries';
import { ProfileContext } from '../../../App';
import { autoDissmissOnAction } from '../utils/ctaDismissal';
import { SuccessFeedback } from '../components/SuccessFeedback';

interface AddRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  title: string;
  clientName: string;
  location: string;
  summary: string;
}

// Enhanced Dialog with animations
const StyledDialogContent = styled(DialogContent)`
  animation: scaleIn 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  border-radius: 8px;
  max-width: 600px;

  input, textarea {
    transition: border-color 200ms ease-in-out, box-shadow 200ms ease-in-out;

    &:focus {
      border-color: #227B7E;
      box-shadow: 0 0 0 0.25rem rgba(34, 123, 126, 0.15);
    }
  }

  button {
    transition: transform 150ms ease-in-out;

    &:active:not(:disabled) {
      transform: scale(0.97);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    button:active:not(:disabled) {
      transform: none;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormText = styled.p`
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
`;

const InvalidFeedback = styled.div`
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.25rem;
`;

const RequiredStar = styled.span`
  color: #dc3545;
`;

/**
 * AddRoleModal - Quick add role modal with minimal fields
 * Provides sensible defaults for all required backend fields
 */
export function AddRoleModal({ isOpen, onClose, onSuccess }: AddRoleModalProps) {
  const { userProfile } = useContext(ProfileContext);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    clientName: '',
    location: '',
    summary: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch localities for location lookup
  const { data: localitiesData } = useQuery(GET_LOCALITIES, {
    variables: { search: formData.location || 'Sydney' },
    skip: !formData.location,
  });

  const [addRole, { loading }] = useMutation(ADD_ROLE, {
    refetchQueries: [{ query: GET_ROLES, variables: { skip: 0, take: 50, order: [{ updatedAt: 'DESC' }] } }],
  });

  /**
   * Get default locality ID (Sydney, Australia as fallback)
   */
  const getLocalityId = (): number => {
    if (localitiesData?.localities?.items?.length > 0) {
      return localitiesData.localities.items[0].id;
    }
    // Fallback to Sydney, Australia (common default)
    return 20374;
  };

  /**
   * Get sensible defaults for all required fields
   */
  const getDefaults = () => {
    const today = new Date();
    const oneYearLater = new Date();
    oneYearLater.setFullYear(today.getFullYear() + 1);

    return {
      clientName: formData.clientName || 'TBD',
      title: formData.title,
      summary: formData.summary || 'Role details to be confirmed',
      workType: 'PERMANENT',
      salaryMin: 0,
      salaryMax: 0,
      startDate: today.toISOString().split('T')[0],
      termsSigned: false,
      replacementGuarantee: false,
      engagementType: 'FULLTIME',
      feeType: 'PERCENTAGE',
      feeValue: 15, // Standard 15% recruitment fee
      split: 50, // 50/50 split default
      industry: userProfile?.industryPreferences?.[0] || 'Technology',
      placementStartDate: today.toISOString().split('T')[0],
      placementEndDate: oneYearLater.toISOString().split('T')[0],
      workingHours: '38',
      localityId: getLocalityId(),
      state: '',
      suburb: formData.location || '',
      share: false,
      audience: 'PRIVATE',
      currency: 'AUD',
      ownerUserIds: [userProfile?.id].filter(Boolean),
      projectJobBoardIds: [],
    };
  };

  /**
   * Handles form submission
   */
  const handleSubmit = async (closeAfter: boolean) => {
    setError(null);

    // Validation
    if (!formData.title.trim()) {
      setError('Job title is required');
      return;
    }

    try {
      const variables = getDefaults();

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await addRole({ variables });

      // Show success animation
      setShowSuccess(true);

      // Auto-dismiss related CTAs
      autoDissmissOnAction('first_role');

      onSuccess();

      // Wait for success animation, then close or reset
      setTimeout(() => {
        setShowSuccess(false);
        if (closeAfter) {
          onClose();
          // Reset form
          setFormData({ title: '', clientName: '', location: '', summary: '' });
        } else {
          // Reset form for "Add Another"
          setFormData({ title: '', clientName: '', location: '', summary: '' });
        }
      }, 1500);
    } catch (err: any) {
      console.error('Error adding role:', err);
      setError(err.message || 'Failed to add role. Please try again.');
    }
  };

  /**
   * Handles modal close
   */
  const handleClose = () => {
    if (!loading && !showSuccess) {
      setFormData({ title: '', clientName: '', location: '', summary: '' });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <StyledDialogContent>
        <DialogHeader>
          <DialogTitle>Add New Role</DialogTitle>
        </DialogHeader>

        {showSuccess ? (
          <SuccessFeedback
            message="Role added successfully!"
            subtext="You can now match candidates to this role"
          />
        ) : (
          <>
            <div>
              <FormGroup>
                <Label htmlFor="title">
                  Job Title <RequiredStar>*</RequiredStar>
                </Label>
                <Input
                  id="title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Senior Software Engineer"
                  autoFocus
                  className={error === 'Job title is required' ? 'border-red-500' : ''}
                />
                {error === 'Job title is required' && (
                  <InvalidFeedback>Job title is required</InvalidFeedback>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="clientName">Client Name (Optional)</Label>
                <Input
                  id="clientName"
                  type="text"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  placeholder="e.g., Acme Corporation"
                />
                <FormText>
                  Leave blank if not yet known - you can add this later
                </FormText>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="location">Location (Optional)</Label>
                <Input
                  id="location"
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Sydney, Remote, New York"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="summary">Description (Optional)</Label>
                <Textarea
                  id="summary"
                  rows={4}
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Brief overview of the role, responsibilities, and requirements..."
                />
                <FormText>
                  Add a quick summary now, or fill in full details later in settings
                </FormText>
              </FormGroup>

              {error && error !== 'Job title is required' && (
                <Alert variant="destructive" className="mb-3">
                  <AlertDescription>
                    <strong>Unable to add role</strong>
                    <p className="mt-1">{error}</p>
                    <small className="text-xs">
                      You can still edit all details later by going to the role settings.
                    </small>
                  </AlertDescription>
                </Alert>
              )}

              <Alert className="mb-3" style={{ fontSize: '0.875rem' }}>
                <AlertDescription>
                  <strong>💡 Quick Add:</strong> This creates a private role with default values.
                  You can edit all details (salary, fees, etc.) in the role settings later.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSubmit(false)}
                disabled={!formData.title.trim() || loading}
              >
                {loading ? 'Adding...' : 'Add & Create Another'}
              </Button>
              <Button
                onClick={() => handleSubmit(true)}
                disabled={!formData.title.trim() || loading}
              >
                {loading ? 'Adding...' : 'Add Role'}
              </Button>
            </DialogFooter>
          </>
        )}
      </StyledDialogContent>
    </Dialog>
  );
}
